import axios from 'axios';
import deepmerge from 'deepmerge';

import { PRODUCT_LECTURA, PRODUCT_LECTURA_CAP, PRODUCT_READING } from 'root/lib/constants';
import AuthService from 'root/lib/authService';
import { getTimestamp } from 'root/lib/DatetimeManager';
import { persistStateTypes } from '../reducers/persistStateReducer';
import GAME_IDS from '../hub/data/GameIds';
import { ApiResponseError, getApiErrorMessage, handleAuthFailure } from '../api/apiErrors';
import {
  API_URL,
  CLEAR_GAMESTATE_URL,
  CLEAR_VOCAB_PROGRESS_URL,
  GAME_STATE_BULK_URL,
  VOCAB_RESULTS_URL,
} from '../api/endpoints';
import { aquaFetchWithCreds, aquaAxiosWithCreds, callAsyncWithRetry } from '../../lib/utilityFunctions';
import { getErrorActionObject, showStandardErrorPage } from '../components/GlobalErrorHandler/actions';
import { GAMESTATE_VERSION, RETRY_TIMEOUT } from '../hub/data/constants';
import { setSkillMap } from './searchlightActions';

/**
 * Canonical game name for metagame.
 * @constant
 * @type {string}
 */
export const METAGAME_ID = GAME_IDS.METAGAME;

export const setVocabResults = result => ({
  type: persistStateTypes.SET_VOCAB_RESULTS,
  gameId: METAGAME_ID,
  result,
});

/**
 * Canonical game name for metagame.
 * @constant
 * @type {string}
 */
const POST_STATE_KEYS_ERROR_MESSAGE = 'One or more of the keys you are sending to setGameState are invalid';

/**
 * Creates an action for when there is an error retrieving game state. This
 * action is used both in thunks for persist state and in api/adaptvitiy.js
 * @param {string} gameId canonical game name
 * @param {string|object} error error descitpion or object
 * @return {object} action object for error in get state request action
 */
export const requestStateError = (gameId, error) => ({
  type: persistStateTypes.REQUESTERROR_GAMESTATE,
  gameId,
  error,
});

/**
 * Redux Action - Error posting game state
 * @param {string} gameId - Canonical game name
 * @param {string|object} error - Error descitpion or object
 * @return {object} Error in state post action
 */
export const postStateError = (gameId, error) => ({
  type: persistStateTypes.POSTERROR_GAMESTATE,
  gameId,
  error,
});

export const responseErrorHandler = (error, gameId, dispatch, type) => {
  console.log(`An error occured during gameState ${type}`, error);
  if (type === 'GET') {
    dispatch(requestStateError(gameId, error));
  } else {
    dispatch(postStateError(gameId, error));
  }
  // after the dispatch so the error report is on its way before navigation starts
  handleAuthFailure(error);
};

/**
 * Dispatches a successful gameState response. Failures never reach here: axios rejects on
 * non-2xx and saveGameStateToo throws ApiResponseError, so both land in responseErrorHandler.
 */
export const validateResponseHandler = (response, gameId, dispatch, type) => {
  if (type === 'GET') {
    dispatch({
      type: persistStateTypes.REQUESTRESPONSE_GAMESTATE,
      gameId,
      stateData: response,
    });
  } else if (type === 'POST') {
    dispatch({
      type: persistStateTypes.POSTRESPONSE_GAMESTATE,
      gameId,
      msg: response,
    });
  }
};

export const reportGameStateAPIError = (dispatch, error, category, data) => {
  dispatch(getErrorActionObject(METAGAME_ID, getApiErrorMessage(error), category, data, 'api'));
};

/**
 * Redux Thunk Action - Function for getting game state
 * @param {string} gameId - Canonical game name
 * @return {Promise} Game state resolution
 */
export const getGameState = gameId => async (dispatch, getState) => {
  dispatch({
    type: persistStateTypes.REQUEST_GAMESTATE,
    gameId,
  });
  const { persistStateReducer: { studentId } } = getState();
  try {
    const { data } = await aquaAxiosWithCreds(
      `${API_URL}/gameState?userId=${studentId}&gameId=${gameId}`,
      { method: 'GET' },
    );
    validateResponseHandler(data, gameId, dispatch, 'GET');
  } catch (error) {
    reportGameStateAPIError(
      dispatch, error, 'API: gameState', `API: getGameState | ${gameId}`,
    );
    responseErrorHandler(error, gameId, dispatch, 'GET');
  }
};

export const getDemoData = () => async (dispatch, getState) => {
  const { persistStateReducer: { studentId } } = getState();
  const defaultData = { demoProduct: '', demoSeason: '' };
  try {
    const { data } = await aquaAxiosWithCreds(
      `${API_URL}/gameState?userId=${studentId}&gameId=TEACHER_DASHBOARD_SETTINGS`,
      { method: 'GET' },
    );
    const gameState = data.TEACHER_DASHBOARD_SETTINGS.GameData || defaultData;
    return {
      demoProduct: gameState.demoProduct,
      demoSeason: gameState.demoSeason,
    };
  } catch (error) {
    reportGameStateAPIError(
      dispatch, error, 'API: gameState', 'API: getGameState | TEACHER_DASHBOARD_SETTINGS',
    );
    return defaultData;
  }
};

export const saveGameState = (
  gameId,
  { cancelToken, ...gameData } = {},
  isIntermediateSave = false,
  version = GAMESTATE_VERSION,
) => async (dispatch, getState) => callAsyncWithRetry(async () => {
  const { gameDetails } = gameData;
  let { currentQuest } = gameData;
  if (gameDetails || currentQuest) {
    if (currentQuest) {
      convertSkillIdToString(currentQuest);
    } else {
      const { contentServiceQuestId } = gameDetails;
      currentQuest = gameDetails.currentQuest;
      if (currentQuest && contentServiceQuestId) {
        convertSkillIdToString(currentQuest, contentServiceQuestId);
      }
    }
  }
  dispatch({
    type: persistStateTypes.POST_GAMESTATE,
    gameId,
    stateData: gameData,
  });
  const { persistStateReducer: { studentId } } = getState();
  try {
    const { data } = await aquaAxiosWithCreds(`${API_URL}/gameState`, {
      method: 'POST',
      cancelToken,
      data: {
        [gameId]: {
          GameVersion: version,
          ContentVersion: version,
          UserId: studentId,
          GameData: {
            ...gameData,
            gameDetails: {
              ...gameData.gameDetails,
              // add roundSession data from searchlight for use by games with intermediate save
              roundSessionData: !isIntermediateSave ? undefined : {
                ...getState().appSessionReducer.searchlight,
                lastSaveTimestamp: getTimestamp(),
              },
            },
          },
        },
      },
    });
    validateResponseHandler(data, gameId, dispatch, 'POST');
  } catch (error) {
    if (!axios.isCancel(error)) {
      reportGameStateAPIError(dispatch, error, 'API: gameState', `API: saveGameState | ${gameId}`);
      responseErrorHandler(error, gameId, dispatch, 'POST');
      throw error;
    }
  }
});

/**
 * Redux Thunk Action - Function for setting game state
 * @param {string} gameId - Canonical game name
 * @param {number} zapsAvailable - total zaps available to earn
 * @param {number} zapsEarned - total zaps earned
 * @param {number} bitesEarned - total bites earned
 * @param {number} highestLevelEarned - highest level earned in game
 * @param {Object} gameDetails - Object containing game data to persist
 * @return {Promise} Game state resolution
 */
export const setGameState = (
  gameId,
  zapsAvailable,
  zapsEarned,
  bitesEarned,
  highestLevelEarned,
  gameDetails,
) => {
  const validationArray = {
    gameId: typeof gameId === 'string',
    zapsAvailable: typeof zapsAvailable === 'number',
    zapsEarned: typeof zapsEarned === 'number',
    bitesEarned: typeof bitesEarned === 'number',
    highestLevelEarned: typeof highestLevelEarned === 'number',
    gameDetails: typeof gameDetails === 'object',
  };
  const isValid = Object.keys(validationArray).reduce((validity, key) => {
    if (!validity || !validationArray[key]) {
      return false;
    }
    return true;
  }, true);

  if (!isValid) {
    console.error(
      POST_STATE_KEYS_ERROR_MESSAGE,
      'Invalid keys marked "false"',
      validationArray,
    );
    return postStateError(gameId, POST_STATE_KEYS_ERROR_MESSAGE);
  }
  return saveGameState(
    gameId,
    {
      zapsAvailable,
      zapsEarned,
      bitesEarned,
      highestLevelEarned,
      gameDetails,
    },
  );
};

/*
  Works for Teacher Role only
*/
export const clearGameStateInDemoMode = () => async (dispatch) => {
  try {
    await aquaAxiosWithCreds(
      `${CLEAR_GAMESTATE_URL}?exclude=TEACHER_DASHBOARD_SETTINGS,STUDENT_SETTINGS`, {
        method: 'DELETE',
      },
    );
  } catch (error) {
    reportGameStateAPIError(dispatch, error, 'API: gameState', 'API: clearGameStateInDemoMode');
  }
};

/*
* Clears Vocab data on Vocab App side
*/
export const clearVocabProgressData = (questId = '') => async (dispatch) => {
  const businessKey = AuthService.getUserBk();
  try {
    await aquaAxiosWithCreds(
      questId === '' ?
        `${CLEAR_VOCAB_PROGRESS_URL}${businessKey}/delete` :
        `${CLEAR_VOCAB_PROGRESS_URL}${businessKey}/delete?questId=${questId}`,
      {
        method: 'DELETE',
      },
    );
  } catch (error) {
    reportGameStateAPIError(dispatch, error, 'API: gameState', 'API: clearVocabProgressData');
  }
};

/*
 * Clears bucket data fully, same way as Aqua Admin does this
 * Method still not supported for Student role
 * TO-DO: might be possible to use in the future when corresponding API changes are done
*/
export const clearFullGameStateBucketData = bucketsList => async (dispatch, getState) => {
  const body = {
    Students: [{ GameIds: bucketsList }],
  };
  const { persistStateReducer: { studentId } } = getState();
  body.Students[0].UserId = studentId;
  try {
    await aquaAxiosWithCreds(`${GAME_STATE_BULK_URL}`, {
      method: 'DELETE',
      data: JSON.stringify(body),
    });
  } catch (error) {
    reportGameStateAPIError(dispatch, error, 'API: gameState', 'API: clearFullGameStateBucketData');
  }
};

export const fetchVocabResultsData = () => async (dispatch) => {
  const asyncRetryFn = async () => {
    const { data } = await aquaAxiosWithCreds(VOCAB_RESULTS_URL);
    dispatch(setVocabResults(data));
  };

  const asyncRetryConfig = {
    interval: RETRY_TIMEOUT,
    logger: async (error, { isDoneRetrying }) => {
      const message = getApiErrorMessage(error);
      dispatch(getErrorActionObject(
        GAME_IDS.METAGAME,
        message,
        'API: get-recent-quest-words',
        'API: fetchVocabResultsData',
        'api',
      ));
      handleAuthFailure(error);
      if (isDoneRetrying) {
        // This API is part of loading percentage screen.
        // Clear loading UX to show Error screen
        window.reactLoaded();
        // Show Uh Oh error screen
        dispatch(showStandardErrorPage(error.name, message));
      }
    },
  };

  return callAsyncWithRetry(asyncRetryFn, asyncRetryConfig);
};

export const getProductsAvailable = (productsAvailable) => {
  const productsAvailableString = productsAvailable || '';
  const products = productsAvailableString.trim().replace(/product-/g, '').split(',');
  const validProducts = products.filter(p => (p === PRODUCT_LECTURA || p === PRODUCT_READING));
  if (!productsAvailableString || validProducts.length === 0) return [PRODUCT_READING, PRODUCT_LECTURA];
  return products;
};

/**
 * Set authentication data from student login info
 */
export const setAuthData = (payload) => ({
  type: persistStateTypes.SET_AUTH,
  payload,
});

/**
 * The /placement/origin endpoint has the same fields (eg, placedMethod) but different values by product.
 * When we receive these values, we may not know which product the student has selected so we save both products' values
 * eg, placedMethod and placedMethodLectura
 * This function transforms the associated field names to be product agnostic to simplify other parts of app.
 * @param {string} product - product name eg, reading, lectura
 */
export const transformPlacementFields = (product) => (dispatch, getState) => {
  const { persistStateReducer } = getState();
  const productSuffix = product === PRODUCT_LECTURA ? PRODUCT_LECTURA_CAP : '';

  const placementOriginFields = [
    'redirectToIpt',
    'iptType',
    'isPlacementAPT',
    'isPlacementARBA',
    'isFirstLogin',
    'placedMethod',
    'placementPeriod',
    'placementGrade',
    'toy',
    'season',
    'seasonPart',
    'skillMap',
  ].reduce((acc, field) => {
    acc[field] = persistStateReducer[`${field}${productSuffix}`]; // apply product value without suffix
    acc[`${field}${PRODUCT_LECTURA_CAP}`] = undefined; // remove 'Lectura' suffix only
    return acc;
  }, {});

  dispatch({
    type: persistStateTypes.TRANSFORM_PLACEMENT_FIELDS,
    payload: placementOriginFields,
  });
  dispatch(setSkillMap(placementOriginFields.skillMap));
};

export const updateTOY = toy => ({
  type: persistStateTypes.UPDATE_TOY,
  payload: {
    toy,
  },
});

const saveGameStateToo = (
  {
    gameId,
    gameData,
    gameVersion = GAMESTATE_VERSION,
    contentVersion = GAMESTATE_VERSION,
    isIntermediateSave = false,
  } = {},
  fetchInit = {},
) => (dispatch, getState) => callAsyncWithRetry(async () => {
  const { persistStateReducer: { studentId } } = getState();
  const body = {};
  const { gameDetails } = gameData;
  let { currentQuest } = gameData;
  if (gameDetails || currentQuest) {
    if (currentQuest) {
      convertSkillIdToString(currentQuest);
    } else {
      const { contentServiceQuestId } = gameDetails;
      currentQuest = gameDetails.currentQuest;
      if (currentQuest && contentServiceQuestId) {
        convertSkillIdToString(currentQuest, contentServiceQuestId);
      }
    }
  }
  body[gameId] = {
    UserId: studentId,
    GameData: gameData,
    GameVersion: gameVersion,
    ContentVersion: contentVersion,
  };

  if (isIntermediateSave) {
    body[gameId] = deepmerge(body[gameId], {
      GameData: {
        gameDetails: {
          roundSessionData: {
            ...getState().appSessionReducer.searchlight,
            lastSaveTimestamp: getTimestamp(),
          },
        },
      },
    });
  }

  dispatch({
    type: persistStateTypes.POST_GAMESTATE,
    gameId,
    stateData: gameData,
  });

  try {
    const response = await aquaFetchWithCreds(`${API_URL}/gameState`, {
      ...fetchInit,
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new ApiResponseError(response.status, await response.json().catch(() => ({})));
    }
    validateResponseHandler(await response.json(), gameId, dispatch, 'POST');
  } catch (error) {
    // aquaFetch converts a timeout into a TimeoutError, so an AbortError here is only a debounce cancel
    if (error.name !== 'AbortError') {
      reportGameStateAPIError(dispatch, error, 'API: gameState', `API: saveGameState | ${gameId}`);
      responseErrorHandler(error, gameId, dispatch, 'POST');
      throw error;
    }
  }
});

const timeoutIds = {};
const controllers = {};

/**
 * Cancel any queued or in-flight debounced save for a gameId. Use right before
 * an immediate save so an older, previously-queued POST can't land afterward and
 * overwrite the fresher state. Safe only when the immediate save's payload is a
 * superset of the pending one (otherwise the pending changes would be dropped).
 * @param {string} gameId
 */
export const cancelDebouncedSave = (gameId) => {
  clearTimeout(timeoutIds[gameId]);
  delete timeoutIds[gameId];
  controllers[gameId]?.abort();
  delete controllers[gameId];
};

export const debouncedSaveGameState = (gameState, { debounceMs = 1000, ...fetchInit } = {}) => (dispatch) => {
  const { gameId } = gameState;

  clearTimeout(timeoutIds[gameId]);

  timeoutIds[gameId] = setTimeout(() => {
    if (controllers[gameId]) {
      controllers[gameId].abort();
    }

    controllers[gameId] = new AbortController();

    // Each attempt already reported and dispatched its error; nothing awaits this save
    dispatch(saveGameStateToo(gameState, {
      ...fetchInit,
      controller: controllers[gameId],
    })).catch(() => {});
  }, debounceMs);
};

// meant to be a private function for saveGameState and saveGameStateToo, exporting for testing only
export const convertSkillIdToString = (currentQuest, contentServiceQuestId) => {
  const questId = contentServiceQuestId
    ? `quest${contentServiceQuestId}`
    : Object.keys(currentQuest).find(key => /^quest\d+/.test(key));
  const steps = currentQuest[questId]?.steps ?? currentQuest[questId]?.games;
  if (steps) {
    steps.forEach((step, idx) => {
      steps[idx].skillId = `${step.skillId}`;
    });
  }
};

/**
 * Sets values from `/gameLevelConceptMap` endpoint
 * for verifying Embedded Measurement games/levels
 */
export const setGameLevelConceptMap = payload => ({
  type: 'SET_GAME_LEVEL_CONCEPT_MAP',
  payload,
});
